import React from 'react';
import { colors, borders, fonts, patternZone } from './tokens';

export interface PatternItem {
    id: string;
    label: string;
    subtitle?: string;
    pct: number; // 0–100
    pts?: number; // XP earned
    /** 'shadow' = high pct is bad (red), 'engine' = high pct is good (inverted). Defaults to shadow. */
    type?: 'shadow' | 'engine';
    /** Its direction has no completed pass, so there is no score to report. */
    locked?: boolean;
}

interface PatternsListProps {
    patterns: PatternItem[];
    onSelect?: (id: string) => void;
    zeroBlockerLabel?: string;
    zeroResourceLabel?: string;
    /** Shown instead of a percent for patterns in a direction not yet taken. */
    lockedLabel?: string;
}

const PatternsList: React.FC<PatternsListProps> = ({
    patterns,
    onSelect,
    zeroBlockerLabel = 'Not detected',
    zeroResourceLabel = 'No signal yet',
    lockedLabel = 'Not taken',
}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {patterns.map((p, i) => {
            const zone = patternZone(p.pct, p.type);
            let scoreText = `${p.pct}%`;

            if (p.pct === 0) {
                scoreText = zeroBlockerLabel;
                if (p.type === 'engine') scoreText = zeroResourceLabel;
            }
            // Locked wins over both: "not detected" would be a claim we can't make.
            if (p.locked) scoreText = lockedLabel;

            return (
                <button
                    key={p.id}
                    type="button"
                    onClick={() => onSelect?.(p.id)}
                    className="dash-edit__pat-row"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '32px minmax(0, 280px) 1fr minmax(56px, auto) 16px',
                        alignItems: 'center',
                        gap: 18,
                        padding: '14px 0',
                        borderBottom: borders.hair2,
                        background: 'transparent',
                        border: 'none',
                        borderTop: 'none',
                        borderLeft: 'none',
                        borderRight: 'none',
                        color: 'inherit',
                        fontFamily: 'inherit',
                        cursor: onSelect ? 'pointer' : 'default',
                        textAlign: 'left',
                        width: '100%',
                    }}
                >
                    <div
                        className="dash-edit__pat-num"
                        style={{
                            fontSize: 11,
                            color: colors.subtle,
                            fontFamily: fonts.mono,
                            letterSpacing: '0.05em',
                        }}
                    >
                        {String(i + 1).padStart(2, '0')}
                    </div>

                    <div
                        className="dash-edit__pat-text"
                        style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span
                                style={{
                                    fontSize: 14,
                                    color: p.locked ? colors.muted : colors.textPrimary,
                                    fontWeight: 400,
                                    letterSpacing: '-0.005em',
                                }}
                            >
                                {p.label}
                            </span>
                            {p.pts !== undefined && p.pts > 0 && (
                                <span
                                    style={{
                                        fontSize: 10,
                                        fontFamily: fonts.mono,
                                        color: zone.hex,
                                        background: zone.soft,
                                        border: `1px solid ${zone.hex}33`,
                                        padding: '2px 7px',
                                        borderRadius: 999,
                                        letterSpacing: '-0.02em',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    +{p.pts} pts
                                </span>
                            )}
                        </div>
                        {p.subtitle && (
                            <div
                                style={{
                                    fontSize: 12,
                                    color: colors.muted,
                                    lineHeight: 1.4,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {p.subtitle}
                            </div>
                        )}
                    </div>

                    <div
                        className="dash-edit__pat-bar"
                        style={{
                            position: 'relative',
                            height: 2,
                            background: 'rgba(255,255,255,0.04)',
                        }}
                    >
                        {!p.locked && (
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    width: `${p.pct}%`,
                                    background: zone.hex,
                                    height: 2,
                                    boxShadow: `0 0 6px ${zone.hex}66`,
                                }}
                            />
                        )}
                    </div>

                    <div
                        className="dash-edit__pat-score"
                        style={{
                            fontSize: p.locked ? 11 : 13,
                            color: p.locked ? colors.subtle : colors.textPrimary,
                            fontFamily: fonts.mono,
                            letterSpacing: '-0.02em',
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {scoreText}
                    </div>

                    <div
                        className="dash-edit__pat-chevron"
                        style={{ color: colors.subtle, fontSize: 14, lineHeight: 1 }}
                    >
                        ›
                    </div>
                </button>
            );
        })}
    </div>
);

export default PatternsList;
